/**
 * PermissionGuard Component
 *
 * Conditionally renders children based on user permissions.
 * Works with the usePermissions hook to check granular permissions.
 *
 * FEATURES:
 * - Single permission check
 * - Multiple permissions (any/all logic)
 * - Fallback content when denied
 * - Access denied page redirect
 * - Hide vs show with message options
 *
 * USAGE:
 * ```jsx
 * // Simple permission check
 * <PermissionGuard permission="users.view">
 *   <UserList />
 * </PermissionGuard>
 *
 * // Multiple permissions (any)
 * <PermissionGuard permissions={['users.create', 'users.edit']} requireAll={false}>
 *   <UserForm />
 * </PermissionGuard>
 *
 * // With fallback content
 * <PermissionGuard permission="projects.delete" fallback={<UpgradeMessage />}>
 *   <DeleteButton />
 * </PermissionGuard>
 *
 * // Redirect to access denied page
 * <PermissionGuard permission="admin.access" redirectOnDenied>
 *   <AdminPanel />
 * </PermissionGuard>
 * ```
 *
 * @module PermissionGuard
 */

import React from 'react'
import PropTypes from 'prop-types'
import { Navigate } from 'react-router-dom'
import { usePermissions } from '@hooks'

/**
 * PermissionGuard Component
 *
 * Guards content based on user permissions.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render if permitted
 * @param {string} props.permission - Single permission to check (e.g., "users.view")
 * @param {string[]} props.permissions - Multiple permissions to check
 * @param {boolean} props.requireAll - If true, require all permissions. If false, any one is sufficient
 * @param {React.ReactNode} props.fallback - Content to render if denied (default: null)
 * @param {boolean} props.redirectOnDenied - If true, redirect to access denied page
 * @param {string} props.redirectPath - Custom redirect path (default: "/access-denied")
 * @param {boolean} props.showDeniedMessage - If true, show inline access denied message
 * @param {string} props.deniedMessage - Custom denied message
 */
const PermissionGuard = ({
	children,
	permission,
	permissions = [],
	requireAll = false,
	fallback = null,
	redirectOnDenied = false,
	redirectPath = '/access-denied',
	showDeniedMessage = false,
	deniedMessage = 'You do not have permission to view this content.',
}) => {
	const { can, canAny, canAll, isAuthenticated } = usePermissions()

	// If not authenticated, redirect to login
	if (!isAuthenticated) {
		if (redirectOnDenied) {
			return <Navigate to="/login" replace />
		}
		return fallback
	}

	// Determine which permissions to check
	const permsToCheck = permission ? [permission] : permissions

	// If no permissions specified, allow access (use RoleGuard for role-based access)
	if (permsToCheck.length === 0) {
		return <>{children}</>
	}

	// Check permissions
	let hasAccess = false

	if (permsToCheck.length === 1) {
		// Single permission
		hasAccess = can(permsToCheck[0])
	} else if (requireAll) {
		// All permissions required
		hasAccess = canAll(permsToCheck)
	} else {
		// Any permission is sufficient
		hasAccess = canAny(permsToCheck)
	}

	// Access granted
	if (hasAccess) {
		return <>{children}</>
	}

	// Access denied - redirect if specified
	if (redirectOnDenied) {
		return <Navigate to={redirectPath} replace />
	}

	// Access denied - show message if specified
	if (showDeniedMessage) {
		return (
			<div className="permission-denied">
				<p>{deniedMessage}</p>
			</div>
		)
	}

	// Access denied - render fallback
	return fallback
}

PermissionGuard.propTypes = {
	children: PropTypes.node.isRequired,
	permission: PropTypes.string,
	permissions: PropTypes.arrayOf(PropTypes.string),
	requireAll: PropTypes.bool,
	fallback: PropTypes.node,
	redirectOnDenied: PropTypes.bool,
	redirectPath: PropTypes.string,
	showDeniedMessage: PropTypes.bool,
	deniedMessage: PropTypes.string,
}

export default PermissionGuard
